import { getApi } from "../../helpers/auth_helpers";

const base = () => process.env.REACT_APP_SERVERURL || "";

/** Posts that run a studio or the platform. Everyone else (weaver, helper, dyer, taani, mistri…) can be on a making step. */
const NOT_ARTISAN = new Set([
  "SUPER_ADMIN",
  "ADMIN",
  "ORGANIZATION_ADMIN",
  "ORGANIZATION_MANAGER",
  "KARKHANA_SUPERVISOR",
  "STUDIO",
  "COLLECTOR",
  "USER",
]);

export type ArtisanOption = { label: string; value: string; post?: string; code?: string };

let craftRoles: Promise<Map<string, string>> | null = null;

/**
 * Role id → post name for every craft post, read from the server once. The ids differ per database,
 * so they are never written into the code (the old pickers had UAT's ids built in).
 */
export function craftRoleIds(): Promise<Map<string, string>> {
  if (!craftRoles) {
    craftRoles = Promise.resolve(getApi(`${base()}/user/roles?offset=0&limit=500`))
      .then((json: any) => {
        const roles = new Map<string, string>();
        for (const r of json?.data?.docs || []) {
          const name = String(r?.role || "").trim().toUpperCase();
          if (r?._id && name && !NOT_ARTISAN.has(name)) roles.set(String(r._id), name);
        }
        if (!roles.size) craftRoles = null;
        return roles;
      })
      .catch(() => {
        craftRoles = null;
        return new Map<string, string>();
      });
  }
  return craftRoles;
}

/** WEAVER → Weaver, JACQUARD_MISTRI → Jacquard mistri; NAQSEBAND is the designer. */
export function postLabel(post?: string): string {
  if (!post) return "";
  if (post === "NAQSEBAND") return "Designer";
  const words = post.toLowerCase().split("_").filter(Boolean).join(" ");
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** Posts that usually do a step, by words in its name, so the right people come first. */
const STEP_POSTS: [RegExp, string[]][] = [
  [/weav|bun|karigar|loom/i, ["WEAVER"]],
  [/dye|dying|rang/i, ["DYER"]],
  [/design|naksh|naqsh|graph/i, ["NAQSEBAND"]],
  [/jacquard|patta|card/i, ["JACQUARD_MISTRI"]],
  [/tassel|jali|jhalar/i, ["TASSEL"]],
  [/twist/i, ["TWISTER"]],
  [/wash|dhob/i, ["DHOBI"]],
  [/finish|rafu|repair|stain/i, ["RAFUGAR", "STAIN_MASTER", "TASSEL"]],
];

export function postsForStep(title?: string): string[] {
  const text = String(title || "");
  const found = STEP_POSTS.find(([re]) => re.test(text));
  return found ? found[1] : [];
}

export type ArtisanResults = { options: ArtisanOption[]; total: number };

const LIMIT = 25;

async function findUsers(name: string, roleIds: string[], organizationId: string | undefined, roles: Map<string, string>) {
  const qs = new URLSearchParams({
    offset: "0",
    limit: String(LIMIT),
    name: name.trim(),
    role: roleIds.join(","),
    OrganizationId: organizationId || "",
    sortBy: "name",
    orderBy: "1",
  });
  const json: any = await getApi(`${base()}/user/users?${qs.toString()}`);
  const options: ArtisanOption[] = (json?.data?.docs || [])
    .filter((u: any) => u?._id && u?.name)
    .map((u: any) => ({
      label: String(u.name),
      value: String(u._id),
      post: u?.businessRoleDetails?.[0]?.role || roles.get(String(u.role)) || "",
      code: u?.studioId ? String(u.studioId) : "",
    }));
  return { options, total: Number(json?.data?.length) || options.length };
}

/**
 * The studio's people with a craft post whose name, worker code or phone matches `name` (the
 * first 25 by name when it is empty). Karkhana workers are users with these posts, so they show
 * here too. People whose post fits the step (`preferPosts`, e.g. Weaver for Weaving) come first.
 */
export async function searchArtisans(name: string, organizationId?: string, preferPosts: string[] = []): Promise<ArtisanResults> {
  const roles = await craftRoleIds();
  // No posts to search by (the roles call failed): let the picker fall back to the lists it has.
  if (!roles.size) throw new Error("Artisan search is unavailable");
  const preferIds = Array.from(roles.entries())
    .filter(([, post]) => preferPosts.includes(post))
    .map(([id]) => id);
  const [preferred, everyone] = await Promise.all([
    preferIds.length ? findUsers(name, preferIds, organizationId, roles) : Promise.resolve({ options: [], total: 0 }),
    findUsers(name, Array.from(roles.keys()), organizationId, roles),
  ]);
  const seen = new Set(preferred.options.map((o) => o.value));
  const options = [...preferred.options, ...everyone.options.filter((o) => !seen.has(o.value))].slice(0, LIMIT);
  return { options, total: Math.max(everyone.total, options.length) };
}
